import { Banner } from "components/DesignSystem/Banner/Banner";
import { Button } from "components/DesignSystem/Button/Button";
import {
  RndConflict,
  RndConflictResolution,
  UploadTaxReturnConflict,
} from "types/Models/estimatedTax";
import { currency } from "utils/Currency";
import { pluralize } from "utils/pluralize";
import {
  RND_CONFLICT_RESOLUTION_RETURN,
  RND_CONFLICT_SOURCE_BOOKS,
  RND_CONFLICT_SOURCE_SETTINGS,
} from "./constants";

type Props = {
  conflictResult: UploadTaxReturnConflict;
  pendingResolution: RndConflictResolution | null;
  onResolve: (resolution: RndConflictResolution) => void;
};

type Choice = {
  title: string;
  amount: number;
  helperText: string;
  subText?: string;
};

type ChoiceCardProps = Choice & {
  isLoading: boolean;
  isDisabled: boolean;
  onSelect: () => void;
};

const EXISTING_SOURCE_LABEL: Record<RndConflict["source"], string> = {
  SETTINGS: "Settings",
  BOOKS: "your books",
};

const getClosingsCount = (conflict: RndConflict): number => {
  if (conflict.source !== RND_CONFLICT_SOURCE_BOOKS) return 0;
  return conflict.existing_entries.reduce(
    (total, entry) => total + entry.closings_count,
    0
  );
};

const getChoice = (
  option: RndConflictResolution,
  conflict: RndConflict
): Choice => {
  const keepExistingHelperText = `Nothing from this return will be added for ${conflict.tax_year} R&D.`;

  if (option === RND_CONFLICT_RESOLUTION_RETURN) {
    return {
      title: "Keep amount from filed return",
      amount: conflict.return_amount,
      helperText: `Replaces the ${conflict.tax_year} R&D amount from ${
        EXISTING_SOURCE_LABEL[conflict.source]
      }.`,
    };
  }

  if (option === RND_CONFLICT_SOURCE_BOOKS) {
    const closingsCount = getClosingsCount(conflict);

    return {
      title: "Keep amount from books",
      amount: conflict.existing_amount,
      helperText: keepExistingHelperText,
      subText:
        closingsCount > 0
          ? `From ${pluralize(
              closingsCount,
              "financial closing",
              "financial closings"
            )}`
          : undefined,
    };
  }

  return {
    title: "Keep amount from Settings",
    amount: conflict.existing_amount,
    helperText: keepExistingHelperText,
  };
};

const ChoiceCard = ({
  title,
  amount,
  helperText,
  subText,
  isLoading,
  isDisabled,
  onSelect,
}: ChoiceCardProps) => (
  <div className="t-flex t-flex-1 t-flex-col t-gap-3 t-rounded-lg t-border t-border-solid t-border-neutral-10 t-p-4">
    <div className="t-flex t-flex-col t-gap-0.5">
      <span className="t-text-subtext t-text-text-100">
        {currency({ amount, maximumFractionDigits: 0 })}
      </span>
      {subText && (
        <span className="t-text-body-sm t-text-text-30">{subText}</span>
      )}
    </div>
    <p className="t-m-0 t-flex-1 t-text-body-sm t-text-text-30">{helperText}</p>
    <Button
      type="button"
      customType="secondary"
      size="small"
      block
      isLoading={isLoading}
      disabled={isDisabled}
      onClick={onSelect}
    >
      {title}
    </Button>
  </div>
);

export const RndConflictChoice = ({
  conflictResult,
  pendingResolution,
  onResolve,
}: Props) => {
  const { warning, conflict } = conflictResult;
  const isResolving = pendingResolution !== null;
  const isSettingsConflict = conflict.source === RND_CONFLICT_SOURCE_SETTINGS;
  const options: RndConflictResolution[] = conflict.options;

  return (
    <div className="t-flex t-flex-col t-gap-4">
      <Banner type="warning" description={warning} />

      <div className="t-flex t-gap-3">
        {options.map((option) => (
          <ChoiceCard
            key={option}
            {...getChoice(option, conflict)}
            isLoading={pendingResolution === option}
            isDisabled={isResolving}
            onSelect={() => onResolve(option)}
          />
        ))}
      </div>

      {isSettingsConflict && (
        <p className="t-m-0 t-text-body-sm t-text-text-30">
          The Settings amount excludes the first-year deduction, so it&apos;s
          usually a little lower than the return amount for the same R&amp;D
          spend.
        </p>
      )}
    </div>
  );
};
